import type { FieldTemplateProps } from '@rjsf/utils';
import { CopyPlusIcon, LockIcon, LockOpenIcon } from 'lucide-react';

import type { FormWriterContext } from '@/components/forms/components/form/writer';
import { canLink, getFieldPath } from '@/components/forms/utils/links';

const LOCK_FIELD_TOOLTIP = 'Persist this value across forms';
const UNLOCK_FIELD_TOOLTIP = 'Disable persistence of this value across forms';
const LOCKED_FIELD_TOOLTIP =
  "This field is locked across all forms. To make this field's value unique, disable the persistence lock from the main form.";
const LINK_FIELD_TOOLTIP = 'Persist this value from the main form';
const UNLINK_FIELD_TOOLTIP =
  'Disable persistence of this value from the main form';

const ICON_SIZE = 16 as const;

type Props = Pick<
  FieldTemplateProps<unknown, any, FormWriterContext>,
  'id' | 'formContext'
>;

export const LinkIcon = ({ id, formContext }: Props) => {
  // handle links between forms
  const { links } = formContext || {};
  const {
    id: formId,
    isMainForm,
    onClickLock,
    lockedPaths = [],
    onClickLink,
    linkedPaths = []
  } = links || {};
  const fieldPath = getFieldPath(formId, id!);
  const isLockedField = lockedPaths.includes(fieldPath);
  const isLinkedField = !isLockedField && linkedPaths.includes(fieldPath);
  const canLinkField = canLink(fieldPath);

  if (links && canLinkField) {
    if (isMainForm) {
      const onClick = () => onClickLock?.(fieldPath);

      if (isLockedField) {
        return (
          <div
            className="ml-[5px] cursor-pointer text-primary"
            data-cy="unlock"
            data-is-clickable
            onClick={onClick}
            title={UNLOCK_FIELD_TOOLTIP}
          >
            <LockIcon size={ICON_SIZE} />
          </div>
        );
      }
      return (
        <div
          className="ml-[5px] cursor-pointer text-foreground"
          data-cy="lock"
          onClick={onClick}
          title={LOCK_FIELD_TOOLTIP}
        >
          <LockOpenIcon size={ICON_SIZE} />
        </div>
      );
    }
    if (isLockedField) {
      return (
        <div className="ml-[5px] text-primary" title={LOCKED_FIELD_TOOLTIP}>
          <LockIcon size={ICON_SIZE} />
        </div>
      );
    }

    const onClick = () => onClickLink?.(fieldPath);

    if (isLinkedField) {
      return (
        <div
          className="ml-[5px] cursor-pointer text-foreground"
          data-cy="unlink"
          onClick={onClick}
          title={UNLINK_FIELD_TOOLTIP}
        >
          <CopyPlusIcon size={ICON_SIZE} />
        </div>
      );
    }
    return (
      <div
        className="ml-[5px] cursor-pointer text-primary"
        data-cy="link"
        onClick={onClick}
        title={LINK_FIELD_TOOLTIP}
      >
        <CopyPlusIcon size={ICON_SIZE} />
      </div>
    );
  }

  return null;
};
